import { Div } from "../../../../../base/components/native/div"
import { Input } from "../../../../../base/components/native/input"
import {
    baseStyle, chipRemoveStyle, chipStyle, dropdownStyle, fieldStyle, inputStyle,
    spacerStyle, suggestionActiveStyle, suggestionStyle, titleStyle, wrapStyle
} from "./tags-input.style"

const normalize = (raw: string) => raw.trim().replace(/^#/, '')

const MAX_SUGGESTIONS = 10

export const TagsInput = (initial: string[] = []) => {

    const base = Div()
    base.cssClass(baseStyle)

    const title = Div('برچسب‌ها')
    title.cssClass(titleStyle)
    base.append(title)

    const spacer = Div('')
    spacer.cssClass(spacerStyle)
    base.append(spacer)

    const wrap = Div()
    wrap.cssClass(wrapStyle)
    base.append(wrap)

    const field = Div()
    field.cssClass(fieldStyle)
    wrap.append(field)

    const chips = Div()
    chips.style({ display: 'contents' })
    field.append(chips)

    const input = Input('برچسب جدید را تایپ کنید و Enter بزنید', 'text')
    input.cssClass(inputStyle)
    field.append(input)

    const dropdown = Div()
    dropdown.cssClass(dropdownStyle)
    wrap.append(dropdown)

    let tags: string[] = []
    let suggestions: string[] = []
    let matches: string[] = []
    let activeIdx = -1

    const renderChips = () => {
        chips.empty()
        tags.forEach((tag, i) => {
            const chip = Div()
            chip.cssClass(chipStyle)

            const label = Div('#' + tag)
            chip.append(label)

            const remove = Div('×')
            remove.cssClass(chipRemoveStyle)
            remove.el.onclick = (e: Event) => {
                e.stopPropagation()
                tags.splice(i, 1)
                renderChips()
                renderDropdown()
            }
            chip.append(remove)

            chips.append(chip)
        })
    }

    const computeMatches = (query: string): string[] => {
        const selectedLower = new Set(tags.map(t => t.toLowerCase()))
        const q = query.trim().replace(/^#/, '').toLowerCase()
        const all = suggestions.filter(s => !selectedLower.has(s.toLowerCase()))
        if (!q) return all.slice(0, MAX_SUGGESTIONS)
        const starts: string[] = []
        const contains: string[] = []
        for (const s of all) {
            const lower = s.toLowerCase()
            if (lower.startsWith(q)) starts.push(s)
            else if (lower.includes(q)) contains.push(s)
        }
        return starts.concat(contains).slice(0, MAX_SUGGESTIONS)
    }

    const renderDropdown = () => {
        matches = computeMatches(input.getValue())
        if (activeIdx >= matches.length) activeIdx = matches.length - 1
        dropdown.empty()
        const isFocused = document.activeElement === input.el
        if (matches.length === 0 || !isFocused) {
            dropdown.style({ display: 'none' })
            return
        }
        dropdown.style({ display: 'block' })
        matches.forEach((s, i) => {
            const row = Div('#' + s)
            row.cssClass(i === activeIdx ? suggestionActiveStyle : suggestionStyle)
            row.el.onmousedown = (e: Event) => {
                e.preventDefault()
                pick(s)
            }
            row.el.onmouseenter = () => {
                activeIdx = i
                renderDropdown()
            }
            dropdown.append(row)
        })
    }

    const commit = (raw: string) => {
        const value = normalize(raw)
        if (!value) return
        if (tags.some(t => t.toLowerCase() === value.toLowerCase())) return
        const existing = suggestions.find(s => s.toLowerCase() === value.toLowerCase())
        tags.push(existing || value)
        renderChips()
    }

    const pick = (suggestion: string) => {
        commit(suggestion)
        input.clear()
        activeIdx = -1
        renderDropdown()
        input.focus()
    }

    field.el.onclick = () => input.focus()

    input.on('focus', () => {
        renderDropdown()
    })

    input.on('blur', () => {
        commit(input.getValue())
        input.clear()
        dropdown.style({ display: 'none' })
    })

    input.on('input', () => {
        activeIdx = -1
        renderDropdown()
    })

    input.on('key-enter', (payload: { event: KeyboardEvent, value: string }) => {
        payload.event.preventDefault()
        if (activeIdx >= 0 && matches[activeIdx]) {
            pick(matches[activeIdx])
        } else {
            commit(payload.value)
            input.clear()
            renderDropdown()
        }
    })

    input.on('key', (payload: { event: KeyboardEvent, key: string, value: string }) => {
        if (payload.key === ',' || payload.key === ' ') {
            payload.event.preventDefault()
            if (activeIdx >= 0 && matches[activeIdx]) {
                pick(matches[activeIdx])
            } else {
                commit(payload.value)
                input.clear()
                renderDropdown()
            }
        }
    })

    input.on('key-backspace', () => {
        if (input.getValue() === '' && tags.length > 0) {
            tags.pop()
            renderChips()
            renderDropdown()
        }
    })

    input.on('key-arrow-down', (e: KeyboardEvent) => {
        if (matches.length === 0) return
        e.preventDefault()
        activeIdx = (activeIdx + 1) % matches.length
        renderDropdown()
    })

    input.on('key-arrow-up', (e: KeyboardEvent) => {
        if (matches.length === 0) return
        e.preventDefault()
        activeIdx = activeIdx <= 0 ? matches.length - 1 : activeIdx - 1
        renderDropdown()
    })

    input.on('key-escape', () => {
        dropdown.style({ display: 'none' })
        activeIdx = -1
    })

    tags = (initial || []).map(normalize).filter(Boolean)
    renderChips()

    return Object.assign(base, {
        getValue: () => tags.slice(),
        setValue: (next: string[] = []) => {
            tags = (next || []).map(normalize).filter(Boolean)
            renderChips()
            renderDropdown()
        },
        setSuggestions: (next: string[] = []) => {
            const seen = new Set<string>()
            suggestions = []
            for (const raw of next) {
                if (typeof raw !== 'string') continue
                const t = raw.trim().replace(/^#/, '')
                if (!t) continue
                const key = t.toLowerCase()
                if (seen.has(key)) continue
                seen.add(key)
                suggestions.push(t)
            }
            renderDropdown()
        },
    })
}
